import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Clock, ArrowLeft, Swords, Gamepad2 } from "lucide-react";
import { computerOpponents, BotData, getBotSkillLevel } from "@/lib/bot-constants";
import { useState, useEffect } from "react";
import { AvatarDisplay } from "@/components/AvatarDisplay";

interface BotChallengeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bots: BotData[];
  selectedBot?: BotData | null;
  onChallenge: (bot: BotData, timeControl: string, playerColor: 'white' | 'black', isRated: boolean) => void;
}

const timeControls = [
  { label: "1+0", value: "1+0", description: "Bullet", icon: "⚡" },
  { label: "3+0", value: "3+0", description: "Blitz", icon: "🔥" },
  { label: "5+0", value: "5+0", description: "Blitz", icon: "⭐" },
  { label: "10+0", value: "10+0", description: "Rapid", icon: "♟️" },
  { label: "15+10", value: "15+10", description: "Rapid", icon: "⏰" },
  { label: "30+0", value: "30+0", description: "Classical", icon: "👑" },
];

type DialogStep = 'bot-selection' | 'time-control' | 'color-selection';

export function BotChallengeDialog({ open, onOpenChange, bots, selectedBot, onChallenge }: BotChallengeDialogProps) {
  const [currentSelectedBot, setCurrentSelectedBot] = useState<BotData | null>(selectedBot || null);
  const [selectedTimeControl, setSelectedTimeControl] = useState<string | null>(null);
  const [dialogStep, setDialogStep] = useState<DialogStep>(selectedBot ? 'time-control' : 'bot-selection');
  const [isRated, setIsRated] = useState(true);

  // Reset state when dialog opens/closes or selectedBot changes
  useEffect(() => {
    if (selectedBot) {
      setCurrentSelectedBot(selectedBot);
      setDialogStep('time-control');
    } else {
      setCurrentSelectedBot(null);
      setDialogStep('bot-selection');
    }
    setSelectedTimeControl(null);
    setIsRated(true);
  }, [selectedBot, open]);

  const handleBotSelect = (bot: BotData) => {
    setCurrentSelectedBot(bot);
    setDialogStep('time-control');
  };

  const handleTimeControlSelect = (timeControl: string) => {
    setSelectedTimeControl(timeControl);
    setDialogStep('color-selection');
  };

  const handleBackToBotSelection = () => {
    setDialogStep('bot-selection');
    setSelectedTimeControl(null);
  };

  const handleBackToTimeControl = () => {
    setDialogStep('time-control');
  };

  const handleColorSelect = (color: 'white' | 'black' | 'random') => {
    if (!currentSelectedBot || !selectedTimeControl) return;
    
    const finalColor = color === 'random' 
      ? (Math.random() < 0.5 ? 'white' : 'black') 
      : color;
    
    onChallenge(currentSelectedBot, selectedTimeControl, finalColor, isRated);
  };

  const handleDialogClose = () => {
    onOpenChange(false);
    // Reset to initial state
    setTimeout(() => {
      setCurrentSelectedBot(selectedBot || null);
      setDialogStep(selectedBot ? 'time-control' : 'bot-selection');
      setSelectedTimeControl(null);
      setIsRated(true);
    }, 150);
  };

  const getStepTitle = () => {
    switch (dialogStep) {
      case 'bot-selection':
        return 'Choose Your Opponent';
      case 'time-control':
        return `Challenge ${currentSelectedBot?.name}`;
      case 'color-selection':
        return 'Choose Your Side';
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {dialogStep !== 'bot-selection' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-muted"
                onClick={dialogStep === 'color-selection' ? handleBackToTimeControl : handleBackToBotSelection}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {getStepTitle()}
          </DialogTitle>
        </DialogHeader>
        
        {/* Bot Selection Step */}
        {dialogStep === 'bot-selection' && (
          <div className="py-4">
            <div className="grid grid-cols-2 gap-4">
              {bots.map((bot) => (
                <Card 
                  key={bot.name}
                  className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 border-2 hover:border-primary/50"
                  onClick={() => handleBotSelect(bot)}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center gap-3 text-center">
                      <AvatarDisplay
                        username={bot.name}
                        avatarConfig={bot.avatarConfig}
                        size="xl"
                        showAnimation={false}
                      />
                      <div>
                        <h5 className="font-semibold text-base">{bot.name}</h5>
                        <p className="text-sm text-muted-foreground mb-2">{bot.description}</p>
                        <Badge variant="outline" className="text-sm">
                          {bot.elo} ELO • {getBotSkillLevel(bot.elo)}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Time Control Selection Step */}
        {dialogStep === 'time-control' && currentSelectedBot && (
          <div className="py-4 space-y-6">
            {/* Selected Bot Display */}
            <Card className="bg-muted/50 border-2">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <AvatarDisplay
                    username={currentSelectedBot.name}
                    avatarConfig={currentSelectedBot.avatarConfig}
                    size="lg"
                    showAnimation={false}
                  />
                  <div>
                    <h3 className="font-semibold text-lg">{currentSelectedBot.name}</h3>
                    <p className="text-sm text-muted-foreground italic">"{currentSelectedBot.personality}"</p>
                    <Badge variant="outline" className="text-sm mt-1">
                      {currentSelectedBot.elo} ELO • {getBotSkillLevel(currentSelectedBot.elo)}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Time Controls */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <h4 className="font-semibold text-lg">Select Time Control</h4>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {timeControls.map((control) => (
                  <Button
                    key={control.value}
                    variant="outline"
                    className="flex flex-col h-auto p-4 hover:bg-muted border-2 hover:border-primary/50 hover:scale-105 transition-all duration-200"
                    onClick={() => handleTimeControlSelect(control.value)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{control.icon}</span>
                      <span className="font-semibold text-base">{control.label}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">{control.description}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Color Selection Step */}
        {dialogStep === 'color-selection' && currentSelectedBot && selectedTimeControl && (
          <div className="py-4 space-y-6">
            {/* Game Summary */}
            <Card className="bg-muted/50 border-2">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <AvatarDisplay
                      username={currentSelectedBot.name}
                      avatarConfig={currentSelectedBot.avatarConfig}
                      size="md"
                      showAnimation={false}
                    />
                    <div>
                      <h3 className="font-semibold">{currentSelectedBot.name}</h3>
                      <Badge variant="outline" className="text-xs">
                        {currentSelectedBot.elo} ELO
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="secondary" className="text-sm">
                      {selectedTimeControl}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rated/Unrated Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
              <div className="flex items-center gap-3">
                {isRated ? (
                  <Swords className="w-5 h-5 text-orange-500" />
                ) : (
                  <Gamepad2 className="w-5 h-5 text-blue-500" />
                )}
                <div>
                  <Label htmlFor="rated-toggle" className="font-semibold cursor-pointer">
                    {isRated ? 'Rated Game' : 'Casual Game'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {isRated 
                      ? 'This game will affect your Elo rating' 
                      : 'This game will not affect your rating'}
                  </p>
                </div>
              </div>
              <Switch
                id="rated-toggle"
                checked={isRated}
                onCheckedChange={setIsRated}
              />
            </div>

            {/* Color Selection */}
            <div className="space-y-4">
              <h4 className="font-semibold text-lg text-center">Choose Your Color</h4>
              
              <div className="grid grid-cols-3 gap-4">
                {/* Play as White */}
                <Card 
                  className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 border-2 hover:border-primary/50"
                  onClick={() => handleColorSelect('white')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="text-5xl mb-3">⬜</div>
                    <h5 className="font-semibold">White</h5>
                    <p className="text-xs text-muted-foreground">Move first</p>
                  </CardContent>
                </Card>

                {/* Random */}
                <Card 
                  className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 border-2 hover:border-primary/50"
                  onClick={() => handleColorSelect('random')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="text-5xl mb-3">🎲</div>
                    <h5 className="font-semibold">Random</h5>
                    <p className="text-xs text-muted-foreground">Let fate decide</p>
                  </CardContent>
                </Card>

                {/* Play as Black */}
                <Card 
                  className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 border-2 hover:border-primary/50"
                  onClick={() => handleColorSelect('black')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="text-5xl mb-3">⬛</div>
                    <h5 className="font-semibold">Black</h5>
                    <p className="text-xs text-muted-foreground">Move second</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleDialogClose}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
